import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { getTokenIntel, getIntelAccessToken, getDeepTokenIntel } from "../services/api.js";
import {
  ShieldCheckIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  LockIcon,
  SearchIcon,
  ActivityIcon,
} from "../components/Icons.js";
import { LEGAL_DISCLAIMER } from "../data/metric-explanations.js";
import { formatSafetyScore } from "../lib/scoreFormatter.js";
import { ConfidencePanel, VerdictExplanation, RiskBreakdown, EliteIntelPanel, AdvancedForensics, ProInvestorSummary, ProCoreMetrics, EliteSectionDivider } from "../components/intel/index.js";
import type { WalletIntelData, LiquidityIntelData, ApprovalIntelData, ContractIntelData, HolderIntelData, ProviderPanelData, FundingAttributionData, LockerIntelData, SocialIntelData } from "../components/intel/index.js";
import { RISK_LABELS, getRiskLabel } from "../lib/riskLabels.js";
import { normalizeIntel, lookupKnownToken } from "../utils/intelNormalizer.js";
import { buildCoreMetrics, buildRiskDomains } from "../lib/coreMetricsBuilder.js";

/* ------------------------------------------------------------------ */
/* Types (mirrors API design doc)                                     */
/* ------------------------------------------------------------------ */

type RiskLevel = "GREEN" | "YELLOW" | "RED" | "UNKNOWN";
/** Risk summary — factual characterization, not investment advice */
type RiskSummary = "LOW_RISK" | "MODERATE_RISK" | "HIGH_RISK" | "ANALYZING";

interface IntelProject {
  id?: string;
  name: string | null;
  ticker: string | null;
  deployer: string | null;
  liquidityUsd: number | null;
  launchTime?: number | null;
  socialScore?: number;
  contractRisk?: number;
  overallScore?: number;
  outcome?: string;
}

interface IntelContractSafety {
  score: number;
  buyTaxPct: number;
  sellTaxPct: number;
  maxTxPct: number;
  maxWalletPct: number;
  hasMintFunction: boolean;
  hasBlacklist: boolean;
  hasPauseFunction: boolean;
  ownershipRenounced: boolean;
}

interface IntelLiquidity {
  lpLocked: boolean;
  lpLockMonths: number | null;
  lpLockPlatform: string | null;
  initialLiquidityUsd: number | null;
  currentLiquidityUsd: number | null;
  liquidityChange24h: number | null;
}

interface IntelHolders {
  top10HoldersPct: number;
  top20HoldersPct: number;
  devHoldsPct: number;
  burnedPct: number;
}

interface IntelSmartMoney {
  smartMoneyCount: number;
  whaleCount: number;
  clusterConviction: number;
  holders: Array<{
    address: string;
    type: string;
    label: string;
    conviction: number;
  }>;
}

interface IntelSocial {
  xMentionsPerHour: number | null;
  xFollowers: number | null;
  xEngagementRate: number | null;
  telegramMembers: number | null;
  telegramGrowthRate: number | null;
  discordMembers: number | null;
  websiteAgeDays: number | null;
  githubCommits30d: number | null;
}

interface IntelNetwork {
  upstreamFunders: Array<{ address: string; label?: string }>;
  downstreamDistributors: Array<{ address: string; label?: string }>;
}

interface IntelSignal {
  type: string;
  severity: "info" | "warning" | "critical";
  message: string;
}

interface IntelPreviewData {
  chain: string;
  token: string;
  score: number;
  riskLevel: RiskLevel;
  headline: string;
  project: IntelProject;
  cachedAt: string;
}

interface IntelDeepData extends IntelPreviewData {
  riskSummary?: RiskSummary;
  /** @deprecated mapped from legacy API field */
  recommendation?: string;
  summary: string;
  contractSafety: IntelContractSafety;
  liquidity: IntelLiquidity;
  holders: IntelHolders;
  smartMoney: IntelSmartMoney | null;
  social: IntelSocial;
  network: IntelNetwork | null;
  signals: IntelSignal[];
  risks: string[];
  positives: string[];
  /* Elite III fields */
  confidence?: number;
  confidenceLevel?: string;
  confidenceFactors?: ProviderPanelData;
  walletIntel?: WalletIntelData;
  liquidityIntel?: LiquidityIntelData;
  approvalIntel?: ApprovalIntelData;
  contractIntel?: ContractIntelData;
  holderIntel?: HolderIntelData;
  fundingAttribution?: FundingAttributionData;
  lockerIntel?: LockerIntelData;
  socialIntel?: SocialIntelData;
  analysisStatus?: string;
}

interface IntelLockedSection {
  id: string;
  label: string;
  icon: string;
}

interface IntelTeasers {
  lockedSections: IntelLockedSection[];
  findingsCount: number;
  message: string;
}

interface IntelResponse {
  ok: boolean;
  tier: "preview" | "deep";
  data: IntelPreviewData | IntelDeepData;
  meta: {
    cached: boolean;
    latencyMs: number;
    source: string;
    analysisVersion?: string;
  };
  upgradeHint?: {
    message: string;
    action: string;
  };
  teasers?: IntelTeasers;
}

/* ------------------------------------------------------------------ */
/* Chain metadata                                                     */
/* ------------------------------------------------------------------ */

const CHAIN_META: Record<string, { label: string; color: string }> = {
  eth: { label: "Ethereum", color: "#627eea" },
  sol: { label: "Solana", color: "#9945ff" },
  base: { label: "Base", color: "#0052ff" },
  bnb: { label: "BNB Chain", color: "#f3ba2f" },
  avax: { label: "Avalanche", color: "#e84142" },
};

const RISK_COLORS: Record<RiskLevel, string> = {
  GREEN: "#27ae60",
  YELLOW: "#f39c12",
  RED: "#e74c3c",
  UNKNOWN: "#95a5a6",
};

// RISK_LABELS imported from ../lib/riskLabels.js

const RISK_ICONS: Record<RiskLevel, typeof ShieldCheckIcon> = {
  GREEN: ShieldCheckIcon,
  YELLOW: AlertTriangleIcon,
  RED: AlertTriangleIcon,
  UNKNOWN: AlertTriangleIcon,
};

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function truncAddr(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function fmtUsd(val: number | null | undefined): string {
  if (val == null) return "No data available yet";
  return "$" + val.toLocaleString("en-US", { maximumFractionDigits: 0 });
}


function asDeep(
  tier: "preview" | "deep",
  data: IntelPreviewData | IntelDeepData
): IntelDeepData | null {
  return tier === "deep" ? (data as IntelDeepData) : null;
}

// buildCoreMetrics, buildRiskDomains imported from ../lib/coreMetricsBuilder.js

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

function ScoreCircle({
  score,
  riskLevel,
  size = 120,
}: {
  score: number;
  riskLevel: RiskLevel;
  size?: number;
}) {
  const color = RISK_COLORS[riskLevel];
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(score / 10, 1);
  const offset = circumference * (1 - pct);

  return (
    <div className="intel-score-circle" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="8"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <span className="intel-score-value" style={{ color }}>
        {score.toFixed(1)}
      </span>
    </div>
  );
}


function MetricRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="intel-metric">
      <span className="intel-metric-label">{label}</span>
      <span className="intel-metric-value">{value}</span>
    </div>
  );
}


/** Tier badge shown in the header */
function TierBadge({ userTier, deepLoading }: { userTier: "unknown" | "free" | "pro"; deepLoading: boolean }) {
  if (deepLoading) {
    return (
      <span className="intel-tier-badge" style={{ background: "rgba(255,255,255,0.08)", color: "#999" }}>
        Loading Pro Intel...
      </span>
    );
  }
  if (userTier === "pro") {
    return (
      <span className="intel-tier-badge" style={{ background: "rgba(39,174,96,0.15)", color: "#27ae60", borderColor: "#27ae60" }}>
        Pro Intel
      </span>
    );
  }
  return (
    <span className="intel-tier-badge" style={{ background: "rgba(243,156,18,0.15)", color: "#f39c12", borderColor: "#f39c12" }}>
      Preview
    </span>
  );
}

/** Blurred placeholder content per locked section */
const LOCKED_PLACEHOLDER_CONTENT: Record<string, React.ReactNode> = {
  contractSafety: (
    <div className="intel-grid intel-grid-3">
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Buy Tax</span><span className="intel-metric-value">{"███"} %</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Sell Tax</span><span className="intel-metric-value">{"███"} %</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Sellability Score</span><span className="intel-metric-value">{"█.█"}/10</span></div></div>
    </div>
  ),
  liquidity: (
    <div className="intel-grid intel-grid-3">
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">LP Locked</span><span className="intel-metric-value">{"███"}</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Current Liquidity</span><span className="intel-metric-value">${"███,███"}</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">24h Change</span><span className="intel-metric-value">{"██.█"}%</span></div></div>
    </div>
  ),
  holders: (
    <div className="intel-grid intel-grid-4">
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Top 10</span><span className="intel-metric-value">{"██"}%</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Top 20</span><span className="intel-metric-value">{"██"}%</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Dev</span><span className="intel-metric-value">{"██"}%</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Burned</span><span className="intel-metric-value">{"██"}%</span></div></div>
    </div>
  ),
  smartMoney: (
    <div className="intel-grid intel-grid-3">
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Smart Money</span><span className="intel-metric-value">{"█"} wallets detected</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Whale Count</span><span className="intel-metric-value">{"█"} whales</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Conviction</span><span className="intel-metric-value">{"█.█"}/10</span></div></div>
    </div>
  ),
  social: (
    <div className="intel-grid intel-grid-4">
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">X Mentions</span><span className="intel-metric-value">{"███"}/hr</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Followers</span><span className="intel-metric-value">{"██,███"}</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Telegram</span><span className="intel-metric-value">{"█,███"}</span></div></div>
      <div className="intel-metric-card card"><div className="intel-metric"><span className="intel-metric-label">Discord</span><span className="intel-metric-value">{"███"}</span></div></div>
    </div>
  ),
  network: (
    <div className="intel-grid intel-grid-2">
      <div className="card" style={{ padding: "1rem" }}><div className="intel-metric"><span className="intel-metric-label">Upstream Funders</span><span className="intel-metric-value">{"█"} addresses</span></div></div>
      <div className="card" style={{ padding: "1rem" }}><div className="intel-metric"><span className="intel-metric-label">Downstream</span><span className="intel-metric-value">{"█"} distributors</span></div></div>
    </div>
  ),
};

/** Section icon mapping for teaser locked sections */
const SECTION_ICONS: Record<string, typeof LockIcon> = {
  shield: ShieldCheckIcon,
  chart: ActivityIcon,
  users: SearchIcon,
  brain: CheckCircleIcon,
  globe: SearchIcon,
  network: ActivityIcon,
};

/** Locked section placeholder for free/unauthenticated users — with blur effect */
function LockedSection({ title, icon: Icon }: { title: string; icon?: typeof LockIcon }) {
  const IconComp = Icon ?? LockIcon;
  return (
    <div className="intel-section">
      <h2 className="intel-section-title" style={{ opacity: 0.5 }}>
        <IconComp className="intel-section-icon" aria-hidden="true" />
        {title}
        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", background: "rgba(243,156,18,0.15)", color: "#f39c12", padding: "2px 8px", borderRadius: "4px", fontWeight: 500 }}>
          Pro
        </span>
      </h2>
      <div className="card" style={{ padding: "2rem", textAlign: "center", opacity: 0.6 }}>
        <LockIcon style={{ width: 24, height: 24, margin: "0 auto 0.5rem", display: "block", color: "#f39c12" }} aria-hidden="true" />
        <p style={{ margin: 0, color: "#999" }}>Available with Pro subscription</p>

      </div>
    </div>
  );
}

/** Enhanced locked section with blurred placeholder content for teaser display */
function LockedTeaserSection({
  id,
  label,
  iconName,
}: {
  id: string;
  label: string;
  iconName: string;
}) {
  const IconComp = SECTION_ICONS[iconName] ?? LockIcon;
  const placeholder = LOCKED_PLACEHOLDER_CONTENT[id];

  return (
    <div className="intel-section intel-locked-section">
      <h2 className="intel-section-title">
        <IconComp className="intel-section-icon" aria-hidden="true" />
        {label}
        <span className="intel-locked-badge">PRO</span>
      </h2>
      <div className="intel-locked-content-wrapper">
        <div className="intel-locked-blur">
          {placeholder ?? (
            <div className="card" style={{ padding: "2rem", textAlign: "center" }}>
              <p style={{ margin: 0, color: "#666" }}>{"████ ███ █████"}</p>
            </div>
          )}
        </div>
        <div className="intel-locked-overlay">
          <LockIcon style={{ width: 32, height: 32, color: "#f39c12", marginBottom: "0.5rem" }} aria-hidden="true" />
          <p className="intel-locked-overlay-text">Upgrade to Pro to unlock</p>
        </div>
      </div>
    </div>
  );
}

/** Findings alert banner for teaser */
function TeaserFindingsBanner({
  findingsCount,
  message,
}: {
  findingsCount: number;
  message: string;
}) {
  if (findingsCount === 0) return null;

  return (
    <div className="intel-teaser-findings-banner">
      <AlertTriangleIcon
        style={{ width: 20, height: 20, color: "#e74c3c", flexShrink: 0 }}
        aria-hidden="true"
      />
      <span className="intel-teaser-findings-text">{message}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

export function TokenIntel() {
  const { chain, token } = useParams<{ chain: string; token: string }>();
  const [searchParams] = useSearchParams();
  const { walletAddress, isAuthenticated, token: sessionToken } = useAuth();

  const txContext = searchParams.get("tx");

  /* ---- Preview data state (always fetched, public) ---- */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewResp, setPreviewResp] = useState<IntelResponse | null>(null);

  /* ---- Deep data state (only for authenticated pro users) ---- */
  const [deepResp, setDeepResp] = useState<IntelResponse | null>(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const [userTier, setUserTier] = useState<"unknown" | "free" | "pro">("unknown");

  /* Guard against double-fetch in StrictMode */
  const deepFetchedRef = useRef(false);

  /* ---- Step 1: Always fetch preview (public, no auth) ---- */
  useEffect(() => {
    if (!chain || !token) return;

    setLoading(true);
    setError(null);
    setPreviewResp(null);
    setDeepResp(null);
    setUserTier("unknown");
    deepFetchedRef.current = false;

    getTokenIntel(chain, token)
      .then((data: IntelResponse) => {
        setPreviewResp(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || "Failed to fetch token intelligence");
        setLoading(false);
      });
  }, [chain, token]);

  /* ---- Step 2: If authenticated, try access token -> deep data ---- */
  const fetchDeepIntel = useCallback(async () => {
    if (!chain || !token || !sessionToken || !isAuthenticated) return;
    if (deepFetchedRef.current) return;
    deepFetchedRef.current = true;

    setDeepLoading(true);

    try {
      const accessResult = await getIntelAccessToken(sessionToken);
      setUserTier(accessResult.tier === "pro" ? "pro" : "free");

      if (accessResult.tier === "pro") {
        const deepData = await getDeepTokenIntel(
          chain,
          token,
          accessResult.token,
          walletAddress ?? undefined,
        );
        setDeepResp(deepData);
      }
    } catch (err: any) {
      if (err?.statusCode === 403) {
        setUserTier("free");
      } else {
        setUserTier("unknown");
      }
    } finally {
      setDeepLoading(false);
    }
  }, [chain, token, sessionToken, isAuthenticated, walletAddress]);

  useEffect(() => {
    if (previewResp && isAuthenticated && sessionToken) {
      fetchDeepIntel();
    }
  }, [previewResp, isAuthenticated, sessionToken, fetchDeepIntel]);

  /* ---- Determine display data: deep overrides preview ---- */
  const resp = deepResp ?? previewResp;

  /* Pre-compute intel early so SEO title + header can use it */
  const intel = useMemo(() => {
    if (!resp || resp.tier !== "deep") return null;
    return normalizeIntel(resp.data as unknown as Record<string, unknown>);
  }, [resp]);

  // SEO — resolve token name from intel → API → known-token list → address
  const knownToken = useMemo(() => {
    if (!chain || !token) return null;
    return lookupKnownToken(chain, token);
  }, [chain, token]);

  const tokenName = useMemo(() => {
    if (intel?.tokenName) return intel.tokenName;
    if (resp?.data?.project?.name) return resp.data.project.name;
    if (knownToken?.name) return knownToken.name;
    if (intel?.tokenSymbol) return intel.tokenSymbol;
    if (resp?.data?.project?.ticker) return resp.data.project.ticker;
    if (knownToken?.symbol) return knownToken.symbol;
    if (token) return truncAddr(token);
    return "Token";
  }, [resp, token, intel, knownToken]);

  useEffect(() => {
    document.title = `${tokenName} Risk Analysis \u2014 CryptoGuardians Intel`;
  }, [tokenName]);

  const chainMeta = chain ? CHAIN_META[chain] : null;

  /* ---- Loading state ---- */
  if (loading) {
    return (
      <section className="intel-page">
        <div className="container">
          <div className="intel-loading">
            <SearchIcon className="intel-loading-icon" aria-hidden="true" />
            <h2>Analyzing Token...</h2>
            <p className="intel-loading-sub">
              Scanning contract on {chainMeta?.label ?? chain ?? "unknown chain"}
            </p>
            <div className="intel-loading-bar">
              <div className="intel-loading-bar-fill" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ---- Error state ---- */
  if (error || !resp) {
    return (
      <section className="intel-page">
        <div className="container">
          <div className="intel-error">
            <AlertTriangleIcon
              className="intel-error-icon"
              aria-hidden="true"
            />
            <h2>Analysis Unavailable</h2>
            <p>{error ?? "An unexpected error occurred."}</p>
            <Link to="/" className="btn btn-primary" style={{ marginTop: "1rem" }}>
              Return Home
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const { tier, data, meta, upgradeHint } = resp;
  const { score, riskLevel, headline, project } = data;
  const deep = asDeep(tier, data);
  const RiskIcon = RISK_ICONS[riskLevel];

  return (
    <section className="intel-page">
      <div className="container">
        {/* TX context banner */}
        {txContext && (
          <div className="intel-tx-banner">
            <ActivityIcon
              className="intel-tx-banner-icon"
              aria-hidden="true"
            />
            <span>
              Transaction context:{" "}
              <code className="intel-tx-hash">{truncAddr(txContext)}</code>
            </span>
          </div>
        )}

        {/* ---- Header ---- */}
        <header className="intel-header">
          <div className="intel-header-left">
            <ScoreCircle score={formatSafetyScore(score) ?? 0} riskLevel={riskLevel} />
          </div>
          <div className="intel-header-right">
            <div className="intel-header-badges">
              {chainMeta && (
                <span
                  className="intel-chain-badge"
                  style={{ borderColor: chainMeta.color, color: chainMeta.color }}
                >
                  {chainMeta.label}
                </span>
              )}
              <TierBadge userTier={userTier} deepLoading={deepLoading} />
            </div>
            <h1 className="intel-token-name">
              {intel?.tokenName ?? project.name ?? knownToken?.name ?? "Unverified Token Contract"}
              {(intel?.tokenSymbol ?? project.ticker ?? knownToken?.symbol) && (
                <span className="intel-ticker"> ({intel?.tokenSymbol ?? project.ticker ?? knownToken?.symbol})</span>
              )}
            </h1>
            {intel?.verifiedSafe && (
              <div className="intel-verified-badge-row">
                <span
                  className="intel-verified-badge"
                  title="Verified tokens are confirmed authentic contracts but may still exhibit operational risk indicators."
                >
                  <CheckCircleIcon className="intel-verified-icon" aria-hidden="true" />
                  Verified Token Contract
                </span>
                {intel.verifiedSafeSource && (
                  <span className="intel-verified-source">Source: {intel.verifiedSafeSource}</span>
                )}
              </div>
            )}
            <p className="intel-token-addr">
              <code>{token}</code>
            </p>
            <p className="intel-headline">{headline}</p>
          </div>
        </header>

        {/* ---- Deep loading indicator ---- */}
        {deepLoading && (
          <div style={{ textAlign: "center", padding: "0.75rem", color: "#999", fontSize: "0.875rem" }}>
            Loading Pro Intel...
          </div>
        )}

        {/* ---- Risk Level Banner ---- */}
        <div
          className="intel-risk-banner"
          style={{
            borderColor: RISK_COLORS[riskLevel],
            backgroundColor: `${RISK_COLORS[riskLevel]}10`,
          }}
        >
          <RiskIcon
            className="intel-risk-banner-icon"
            style={{ color: RISK_COLORS[riskLevel] }}
            aria-hidden="true"
          />
          <div>
            <strong style={{ color: RISK_COLORS[riskLevel] }}>
              {RISK_LABELS[riskLevel]}
            </strong>
            <span className="intel-risk-banner-score">
              {" "}
              &mdash; Risk Assessment {(formatSafetyScore(score) ?? 0).toFixed(1)} / 10
            </span>
          </div>
          {riskLevel && (
            <span
              className={"ti-risk-summary ti-risk-summary-" + riskLevel.toLowerCase()}
              title="Risk indicators are based on forensic analysis and may change as new data becomes available."
            >
              {getRiskLabel(riskLevel)}
            </span>
          )}
        </div>
        <p className="intel-risk-disclaimer" style={{ margin: '0.5rem 0 0', fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.4 }}>
          This assessment reflects automated risk indicators, does not eliminate risk, and should not be interpreted as assurance.
        </p>

        {/* ---- Preview Section (always shown) ---- */}
        <div className="intel-section">
          <h2 className="intel-section-title">
            <SearchIcon className="intel-section-icon" aria-hidden="true" />
            Project Overview
          </h2>
          <div className="intel-grid intel-grid-4">
            <div className="intel-metric-card card">
              <MetricRow label="Name" value={intel?.tokenName ?? project.name ?? knownToken?.name ?? "Unknown"} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow label="Ticker" value={intel?.tokenSymbol ?? project.ticker ?? knownToken?.symbol ?? "\u2014"} />
            </div>
            <div className="intel-metric-card card">
              <MetricRow
                label="Deployer"
                value={
                  project.deployer ? (
                    <code>{truncAddr(project.deployer)}</code>
                  ) : (
                    "Unknown"
                  )
                }
              />
            </div>
            <div className="intel-metric-card card">
              <MetricRow
                label="Liquidity"
                value={fmtUsd(project.liquidityUsd)}
              />
            </div>
          </div>
        </div>

        {/* ---- Deep sections (pro only) — v3 Educational + Pro + Elite layout ---- */}
        {intel && (
          <>
            {/* ===== SECTION 1: INVESTOR SUMMARY ===== */}
            <ProInvestorSummary
              score={formatSafetyScore(score) ?? 0}
              riskLevel={riskLevel}
              confidence={intel.analysisConfidence || undefined}
              confidenceLevel={intel.analysisConfidenceLevel || undefined}
              verifiedSafe={intel.verifiedSafe || undefined}
              verifiedSafeSource={intel.verifiedSafeSource || undefined}
              summary={intel.verdictSummary || undefined}
            />

            {/* ===== SECTION 2: CORE SAFETY METRICS ===== */}
            <ProCoreMetrics metrics={buildCoreMetrics(intel)} />

            {/* ===== SECTION 3: WHY THIS VERDICT ===== */}
            <VerdictExplanation
              risks={intel.risks}
              positives={intel.positives}
              signals={intel.signals}
              riskLevel={riskLevel}
            />

            {/* ===== ELITE DIVIDER ===== */}
            <EliteSectionDivider isEliteTier={false} />

            {/* ===== SECTION 4: RISK BREAKDOWN — per-domain grid ===== */}
            <RiskBreakdown domains={buildRiskDomains(intel)} />

            {/* ===== SECTION 5: FORENSIC INTELLIGENCE PANELS ===== */}
            <EliteIntelPanel
              walletIntel={intel.creatorRisk}
              fundingAttribution={intel.creatorFunding}
              contractIntel={intel.contractSafety}
              approvalIntel={intel.contractApproval}
              liquidityIntel={intel.liquiditySafety}
              lockerIntel={intel.liquidityLocker}
              holderIntel={intel.holderRisk}
              socialIntel={intel.socialTrust}
            />

            {/* ===== SECTION 6: ANALYSIS CONFIDENCE ===== */}
            <div className="intel-section-group">
              <h2 className="intel-section-header">Analysis Confidence</h2>
              <p className="intel-section-desc">How reliable is this analysis based on available data sources.</p>
              <ConfidencePanel data={intel.analysisConfidence ? { confidence: intel.analysisConfidence, confidenceLevel: intel.analysisConfidenceLevel || undefined } : null} />
            </div>

            {/* ===== SECTION 7: ADVANCED FORENSICS ===== */}
            <AdvancedForensics
              confidenceFactors={intel.eliteThreat}
              analysisStatus={intel.analysisStatus || undefined}
              meta={meta}
              cachedAt={data.cachedAt}
            />
          </>
        )}

        {/* ---- Locked section placeholders with teasers (when no deep data) ---- */}
        {!deep && !deepLoading && (
          <>
            {/* Findings alert banner from teaser data */}
            {resp.teasers && resp.teasers.findingsCount > 0 && (
              <TeaserFindingsBanner
                findingsCount={resp.teasers.findingsCount}
                message={resp.teasers.message}
              />
            )}

            {/* If we have teaser data from the API, use it for locked sections */}
            {resp.teasers ? (
              <>
                {/* Always show Analysis Summary as a locked section */}
                <LockedSection title="Analysis Summary" icon={ShieldCheckIcon} />
                {/* Render each teaser locked section with blurred placeholder */}
                {resp.teasers.lockedSections.map((section) => (
                  <LockedTeaserSection
                    key={section.id}
                    id={section.id}
                    label={section.label}
                    iconName={section.icon}
                  />
                ))}
                {/* Always show these as basic locked sections */}
                <LockedSection title="Active Signals" />
                <LockedSection title="Risks & Positives" />
              </>
            ) : (
              <>
                <LockedSection title="Analysis Summary" icon={ShieldCheckIcon} />
                <LockedSection title="Contract Analysis" icon={LockIcon} />
                <LockedSection title="Liquidity Analysis" icon={ActivityIcon} />
                <LockedSection title="Holder Distribution" />
                <LockedSection title="Social Metrics" />
                <LockedSection title="Smart Money Signals" />
                <LockedSection title="Network Analysis" />
                <LockedSection title="Active Signals" />
                <LockedSection title="Risks & Positives" />
              </>
            )}
          </>
        )}

        {/* ---- Upgrade CTA ---- */}
        {(userTier === "free" || (!isAuthenticated && !deep)) && (
          <div className="intel-upgrade-cta card card-glow">
            <LockIcon className="intel-upgrade-icon" aria-hidden="true" />
            <div className="intel-upgrade-content">
              <h3>Unlock Deep Intelligence</h3>
              <p>
                {resp.teasers?.message ??
                  upgradeHint?.message ??
                  "Get full contract analysis, holder distribution, smart money signals, and more with a Pro subscription."}
              </p>
            </div>
            <a
              href="/pricing"
              className="btn btn-primary btn-lg intel-locked-cta"
            >
              Upgrade to Pro
            </a>
          </div>
        )}

        {/* ---- Compliance Protection Disclaimer ---- */}
        <div className="ti-disclaimer" style={{ marginTop: "1.5rem", padding: '0.75rem 1rem', background: 'var(--surface-elevated, rgba(255,255,255,0.03))', borderRadius: '8px', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>Compliance Disclaimer</strong>
          {LEGAL_DISCLAIMER}
          {' '}Risk scores and threat levels are generated from automated on-chain analysis and third-party data providers. They do not constitute investment advice or endorsements, do not eliminate risk, and should not be interpreted as assurance. Always do your own research.
        </div>

        {/* ---- Meta footer (only for non-deep view; deep view has it inside Advanced Forensics) ---- */}
        {!deep && (
          <footer className="intel-meta-footer">
            <span>
              Source: {meta.source} &middot; Latency: {meta.latencyMs}ms
              {meta.cached && " (cached)"}
            </span>
            {data.cachedAt && (
              <span>
                Last updated: {new Date(data.cachedAt).toLocaleString()}
              </span>
            )}
          </footer>
        )}
      </div>
    </section>
  );
}
